<template>
  <div class="vpn-settings-page">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.compliance.vpn.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.compliance.vpn.subtitle') }}</p>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-panel">
      <span class="spinner"></span>
      {{ $t('admin.compliance.vpn.loading') }}
    </div>

    <template v-else>
      <!-- Detection Mode -->
      <div class="card">
        <h3 class="card-title">{{ $t('admin.compliance.vpn.section_mode') }}</h3>
        <div class="radio-group">
          <label class="radio-row" :class="{ active: form.vpn_detection_mode === 'disabled' }">
            <input
              v-model="form.vpn_detection_mode"
              type="radio"
              value="disabled"
              class="radio-input"
            />
            <div class="radio-content">
              <div class="radio-label">{{ $t('admin.compliance.vpn.mode_disabled_label') }}</div>
              <div class="radio-desc">{{ $t('admin.compliance.vpn.mode_disabled_desc') }}</div>
            </div>
          </label>
          <label class="radio-row" :class="{ active: form.vpn_detection_mode === 'warn' }">
            <input
              v-model="form.vpn_detection_mode"
              type="radio"
              value="warn"
              class="radio-input"
            />
            <div class="radio-content">
              <div class="radio-label">{{ $t('admin.compliance.vpn.mode_warn_label') }}</div>
              <div class="radio-desc">{{ $t('admin.compliance.vpn.mode_warn_desc') }}</div>
            </div>
          </label>
          <label class="radio-row" :class="{ active: form.vpn_detection_mode === 'block' }">
            <input
              v-model="form.vpn_detection_mode"
              type="radio"
              value="block"
              class="radio-input"
            />
            <div class="radio-content">
              <div class="radio-label">{{ $t('admin.compliance.vpn.mode_block_label') }}</div>
              <div class="radio-desc">{{ $t('admin.compliance.vpn.mode_block_desc') }}</div>
            </div>
          </label>
        </div>
      </div>

      <!-- Detection Thresholds -->
      <div class="card">
        <h3 class="card-title">{{ $t('admin.compliance.vpn.section_thresholds') }}</h3>
        <div class="form-group">
          <label class="form-label">
            {{ $t('admin.compliance.vpn.threshold_label') }} <span class="threshold-value">{{ form.vpn_fraud_score_threshold }}</span>
          </label>
          <input
            v-model.number="form.vpn_fraud_score_threshold"
            type="range"
            min="0"
            max="100"
            step="5"
            class="slider"
          />
          <div class="slider-labels">
            <span>{{ $t('admin.compliance.vpn.threshold_permissive') }}</span>
            <span>{{ $t('admin.compliance.vpn.threshold_mid') }}</span>
            <span>{{ $t('admin.compliance.vpn.threshold_strict') }}</span>
          </div>
          <span class="form-hint">{{ $t('admin.compliance.vpn.hint_threshold') }}</span>
        </div>

        <div class="toggle-row">
          <div>
            <div class="toggle-label">{{ $t('admin.compliance.vpn.block_tor_label') }}</div>
            <div class="toggle-desc">
              Block connections from Tor exit nodes separately from the VPN fraud score threshold.
            </div>
          </div>
          <label class="toggle">
            <input v-model="form.vpn_block_tor" type="checkbox" />
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <!-- IPQualityScore API Key -->
      <div class="card">
        <h3 class="card-title">{{ $t('admin.compliance.vpn.section_api_key') }}</h3>
        <div class="form-group">
          <label class="form-label">{{ $t('admin.compliance.vpn.api_key_label') }}</label>
          <div class="api-key-row">
            <input
              v-model="form.ipqs_api_key"
              :type="showApiKey ? 'text' : 'password'"
              class="form-input"
              placeholder="Enter your IPQualityScore API key"
              autocomplete="off"
            />
            <button
              type="button"
              class="btn btn-secondary btn-icon"
              :title="showApiKey ? 'Hide key' : 'Show key'"
              @click="showApiKey = !showApiKey"
            >
              {{ showApiKey ? '🙈' : '👁' }}
            </button>
          </div>
          <span class="form-hint">Leave blank to keep the current key. The key is stored securely.</span>
        </div>
      </div>

      <!-- Save -->
      <div class="card-footer">
        <button class="btn btn-secondary" :disabled="saving" @click="resetForm">{{ $t('admin.compliance.vpn.btn_cancel') }}</button>
        <button class="btn btn-primary" :disabled="saving" @click="saveSettings">
          {{ saving ? $t('admin.compliance.vpn.btn_saving') : $t('admin.compliance.vpn.btn_save') }}
        </button>
      </div>
      <div v-if="saveMessage" class="save-feedback" :class="saveError ? 'error' : 'success'">
        {{ saveMessage }}
      </div>

      <!-- Detection Log -->
      <div class="card log-card">
        <div class="log-header">
          <h3 class="card-title" style="margin-bottom: 0; border-bottom: none; padding-bottom: 0">
            {{ $t('admin.compliance.vpn.section_log') }}
          </h3>
          <button class="btn btn-secondary btn-sm" :disabled="logsLoading" @click="fetchLogs">
            {{ logsLoading ? $t('admin.compliance.vpn.btn_saving') : $t('admin.compliance.vpn.btn_save') }}
          </button>
        </div>

        <div v-if="logsLoading" class="log-loading">
          <span class="spinner"></span>
          {{ $t('admin.compliance.vpn.log_loading') }}
        </div>

        <div v-else-if="logs.length === 0" class="log-empty">{{ $t('admin.compliance.vpn.log_empty') }}</div>

        <div v-else class="table-wrap">
          <table class="log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>{{ $t('admin.compliance.vpn.col_ip') }}</th>
                <th>{{ $t('admin.compliance.vpn.col_player') }}</th>
                <th>Country</th>
                <th>{{ $t('admin.compliance.vpn.col_score') }}</th>
                <th>{{ $t('admin.compliance.vpn.col_is_vpn') }}</th>
                <th>{{ $t('admin.compliance.vpn.col_is_tor') }}</th>
                <th>{{ $t('admin.compliance.vpn.col_action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.id">
                <td class="mono text-muted">{{ formatDate(log.created_at) }}</td>
                <td class="mono">{{ log.ip_address }}</td>
                <td>
                  <span v-if="log.username" class="username">{{ log.username }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span v-if="log.country_code" class="country-badge">{{ log.country_code }}</span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span class="score-badge" :class="scoreClass(log.fraud_score)">
                    {{ log.fraud_score }}
                  </span>
                </td>
                <td>
                  <span :class="log.is_vpn ? 'flag-yes' : 'flag-no'">
                    {{ log.is_vpn ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span :class="log.is_tor ? 'flag-yes' : 'flag-no'">
                    {{ log.is_tor ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td>
                  <span class="action-badge" :class="log.action_taken === 'block' ? 'action-block' : 'action-warn'">
                    {{ log.action_taken }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'

const { t } = useI18n()

// ----------- Types -----------

interface VpnSettingsForm {
  vpn_detection_mode: string
  vpn_fraud_score_threshold: number
  vpn_block_tor: boolean
  ipqs_api_key: string
}

interface VpnDetectionLog {
  id: number
  ip_address: string
  user_id: number | null
  username: string | null
  is_vpn: boolean
  is_tor: boolean
  fraud_score: number
  country_code: string | null
  action_taken: string
  created_at: string
}

// ----------- State -----------

const loading = ref(true)
const saving = ref(false)
const saveMessage = ref('')
const saveError = ref(false)
const showApiKey = ref(false)
const logsLoading = ref(false)
const logs = ref<VpnDetectionLog[]>([])

const defaultForm: VpnSettingsForm = {
  vpn_detection_mode: 'disabled',
  vpn_fraud_score_threshold: 75,
  vpn_block_tor: true,
  ipqs_api_key: '',
}

const form = ref<VpnSettingsForm>({ ...defaultForm })
let originalForm: VpnSettingsForm = { ...defaultForm }

// ----------- Settings -----------

async function loadSettings(): Promise<void> {
  loading.value = true
  try {
    const response = await apiClient.get('/admin/api/settings')
    const data = (response.data.data ?? {}) as Record<string, unknown>

    form.value.vpn_detection_mode =
      typeof data.vpn_detection_mode === 'string' ? data.vpn_detection_mode : 'disabled'
    form.value.vpn_fraud_score_threshold =
      typeof data.vpn_fraud_score_threshold === 'number'
        ? data.vpn_fraud_score_threshold
        : 75
    form.value.vpn_block_tor =
      data.vpn_block_tor !== undefined ? Boolean(data.vpn_block_tor) : true
    form.value.ipqs_api_key = '' // Never pre-fill from server

    originalForm = { ...form.value }
  } catch {
    saveMessage.value = t('admin.compliance.vpn.load_error')
    saveError.value = true
  } finally {
    loading.value = false
  }
}

async function saveSettings(): Promise<void> {
  saving.value = true
  saveMessage.value = ''
  saveError.value = false

  try {
    const payload: Record<string, unknown> = {
      vpn_detection_mode: form.value.vpn_detection_mode,
      vpn_fraud_score_threshold: form.value.vpn_fraud_score_threshold,
      vpn_block_tor: form.value.vpn_block_tor,
    }

    // Only include API key if the user typed a new one
    if (form.value.ipqs_api_key.trim() !== '') {
      payload.ipqs_api_key = form.value.ipqs_api_key
    }

    await apiClient.put('/admin/api/settings', payload)

    originalForm = { ...form.value }
    form.value.ipqs_api_key = ''
    saveMessage.value = t('admin.compliance.vpn.save_success')
    saveError.value = false
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string } } }
    saveMessage.value = axiosError.response?.data?.message ?? t('admin.compliance.vpn.save_error')
    saveError.value = true
  } finally {
    saving.value = false
  }
}

function resetForm(): void {
  form.value = { ...originalForm }
  saveMessage.value = ''
  saveError.value = false
}

// ----------- Detection Logs -----------

async function fetchLogs(): Promise<void> {
  logsLoading.value = true
  try {
    const response = await apiClient.get('/admin/api/vpn-detection-logs')
    logs.value = (response.data.data ?? []) as VpnDetectionLog[]
  } catch {
    // Silently fail — table may be empty if no detections have occurred
  } finally {
    logsLoading.value = false
  }
}

// ----------- Helpers -----------

function formatDate(timestamp: string): string {
  if (!timestamp) return '—'
  const d = new Date(timestamp)
  return d.toLocaleString(undefined, {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function scoreClass(score: number): string {
  if (score >= 75) return 'score-high'
  if (score >= 40) return 'score-medium'
  return 'score-low'
}

// ----------- Lifecycle -----------

onMounted(async () => {
  await loadSettings()
  await fetchLogs()
})
</script>

<style scoped>
.vpn-settings-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Cards */
.card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  margin-bottom: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: var(--text-primary, #fff);
}

.card-desc {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: -12px 0 20px;
  line-height: 1.5;
}

/* Radio group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radio-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: all 150ms ease;
}

.radio-row:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

.radio-row.active {
  border-color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
}

.radio-input {
  margin-top: 2px;
  accent-color: var(--accent-primary, #00e701);
  flex-shrink: 0;
}

.radio-content {
  flex: 1;
}

.radio-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.radio-desc {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  line-height: 1.5;
}

.radio-desc code {
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
}

/* Slider */
.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 10px;
}

.threshold-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
  text-transform: none;
  letter-spacing: 0;
}

.slider {
  width: 100%;
  accent-color: var(--accent-primary, #00e701);
  cursor: pointer;
  height: 6px;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 6px;
}

.form-hint {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 8px;
}

/* Toggle switch */
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  margin-top: 4px;
}

.toggle-label {
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-weight: 600;
}

.toggle-desc {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 4px;
  max-width: 480px;
}

.toggle {
  position: relative;
  display: inline-flex;
  width: 44px;
  height: 24px;
  background: var(--bg-tertiary, #213743);
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle input {
  display: none;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: var(--text-tertiary, #7f8fa4);
  border-radius: 50%;
  transition: all 150ms ease;
}

.toggle input:checked + .toggle-slider {
  background: var(--accent-primary, #00e701);
  left: 22px;
}

/* API key row */
.api-key-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.form-input {
  flex: 1;
  padding: 10px 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm, 6px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-secondary, #1475e1);
}

.form-input::placeholder {
  color: var(--text-tertiary, #7f8fa4);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00c601);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary, #fff);
}

.btn-icon {
  padding: 9px 12px;
  flex-shrink: 0;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* Card footer */
.card-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
}

/* Feedback */
.save-feedback {
  margin-bottom: 20px;
  padding: 10px 14px;
  border-radius: var(--radius-sm, 6px);
  font-size: 13px;
  font-weight: 500;
}

.save-feedback.success {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.save-feedback.error {
  background: rgba(237, 66, 69, 0.1);
  color: #ed4245;
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* Loading */
.loading-panel,
.log-loading {
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-lg, 12px);
  padding: 48px;
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.log-loading {
  background: transparent;
  padding: 24px;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Log card */
.log-card {
  margin-top: 8px;
}

.log-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.log-empty {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
}

.table-wrap {
  overflow-x: auto;
}

/* Log table */
.log-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.log-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  white-space: nowrap;
}

.log-table td {
  padding: 10px 10px;
  color: var(--text-secondary, #b1bad3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  vertical-align: middle;
}

.log-table tr:last-child td {
  border-bottom: none;
}

.log-table tr:hover td {
  background: rgba(255, 255, 255, 0.02);
}

.mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4) !important;
}

.username {
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.country-badge {
  display: inline-block;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-secondary, #b1bad3);
}

/* Fraud score badge */
.score-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
}

.score-high {
  background: rgba(239, 83, 80, 0.15);
  color: #ef5350;
}

.score-medium {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

.score-low {
  background: rgba(0, 231, 1, 0.1);
  color: #00e701;
}

/* Flag yes/no */
.flag-yes {
  color: #ef5350;
  font-size: 11px;
  font-weight: 600;
}

.flag-no {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 11px;
}

/* Action badge */
.action-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.action-block {
  background: rgba(239, 83, 80, 0.15);
  color: #ef5350;
}

.action-warn {
  background: rgba(255, 167, 38, 0.15);
  color: #ffa726;
}

/* Responsive */
@media (max-width: 768px) {
  .vpn-settings-page {
    padding: 16px;
  }

  .radio-desc {
    font-size: 11px;
  }
}
</style>
